/**
 * TakerShell — the required wrapper for every standalone taker page.
 *
 * Provides:
 *   - Google Fonts (Fraunces + Inter)
 *   - Shared CSS reset and nav/tooltip styles
 *   - TakerNav (three destinations + optional CTA)
 *   - Page background and font defaults
 *   - TakerFooter
 *
 * Every page added to main.tsx MUST use this shell (or SiteNav blocks for
 * config-driven event pages). This is the enforcement point for consistent
 * brand and navigation across all taker surfaces.
 *
 * Usage:
 *   <TakerShell ctaHref={zoomUrl} ctaLabel="Join the Call" footerLabel="For TSB members">
 *     <YourPageContent />
 *   </TakerShell>
 */

import React from 'react';
import TakerNav from './TakerNav';
import TakerFooter from './TakerFooter';

const SHELL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,700;0,9..144,900;1,9..144,400&family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { margin: 0; }
  .cta-btn:hover { background: #d44d1e !important; transform: translateY(-1px); }
  .cta-btn { transition: background 0.15s, transform 0.15s; }
  .tsb-nav-item { position: relative; }
  .tsb-nav-item .tsb-nav-tooltip {
    display: none; position: absolute; top: calc(100% + 10px); left: 50%;
    transform: translateX(-50%); width: 230px; background: #fff;
    border-radius: 10px; padding: 14px 16px;
    box-shadow: 0 8px 28px rgba(10,10,46,0.18), 0 2px 8px rgba(10,10,46,0.10);
    z-index: 100; pointer-events: none;
  }
  .tsb-nav-item .tsb-nav-tooltip::before {
    content: ''; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
    border: 6px solid transparent; border-bottom-color: #fff;
  }
  .tsb-nav-item:hover .tsb-nav-tooltip { display: block; }
  .tsb-nav-link {
    color: rgba(255,255,255,0.72); font-size: 13px; font-weight: 600;
    text-decoration: none; padding: 6px 14px; border-radius: 6px;
    transition: color 0.12s, background 0.12s; white-space: nowrap;
    display: inline-block; letter-spacing: 0.1px;
  }
  .tsb-nav-link:hover { color: #fff; background: rgba(255,255,255,0.1); }
  .tsb-taker-nav {
    background: #0A0A2E; padding: 0 28px; min-height: 64px; gap: 12px;
    display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 50;
  }
  .tsb-taker-nav-logo {
    text-decoration: none; display: flex; align-items: center;
    flex: 0 1 auto; min-width: 0;
  }
  .tsb-taker-nav-links { display: flex; align-items: center; gap: 2px; }
  .tsb-taker-nav-cta { white-space: nowrap; }
  /* Phone: logo + CTA share the first row; the three destinations drop to a
     centred second row. Without this the fixed 64px single row pushed the
     page to ~520px wide at 375px and every page slid sideways. Tooltips are
     hover-only enrichment and, centred under the right-hand link, would
     overflow the screen on tap — so they stay hidden here. */
  @media (max-width: 640px) {
    .tsb-taker-nav { flex-wrap: wrap; padding: 10px 16px 8px; row-gap: 6px; }
    .tsb-taker-nav-links { order: 3; flex: 1 0 100%; justify-content: center; }
    .tsb-taker-nav .tsb-nav-link { padding: 6px 10px; font-size: 12.5px; }
    .tsb-taker-nav .tsb-nav-tooltip { display: none !important; }
  }
`;

interface TakerShellProps {
  children: React.ReactNode;
  /** CTA button in the nav (e.g. Zoom join URL, or "#anchor-id" to smooth-scroll
   *  to an element on this page). Omit for informational pages. */
  ctaHref?: string | null;
  ctaLabel?: string;
  /** Footer label appended after copyright (e.g. "For TSB community members"). */
  footerLabel?: string;
  /** Logo override (e.g. a resolved brand/sub-brand lockup). Defaults to the
   *  TSB logo — omit for the standard TSB-branded pages. */
  logoUrl?: string | null;
  logoAlt?: string;
  logoHref?: string | null;
}

export default function TakerShell({ children, ctaHref, ctaLabel, footerLabel, logoUrl, logoAlt, logoHref }: TakerShellProps) {
  return (
    <>
      <style>{SHELL_STYLES}</style>
      <div style={{ fontFamily: "'Inter', sans-serif", background: '#f8f6f3', color: '#0A0A2E', minHeight: '100vh' }}>
        <TakerNav ctaHref={ctaHref} ctaLabel={ctaLabel} logoUrl={logoUrl} logoAlt={logoAlt} logoHref={logoHref} />
        {children}
        <TakerFooter label={footerLabel} />
      </div>
    </>
  );
}
